"use client";

import { useEffect } from "react";

/**
 * Every third-party script that is not needed for the first paint.
 *
 * AdSense used to sit in `<head>` and gtag/Clarity on window load, which put
 * ~600 KiB of JS in the same window as hydration. Deferring to `load` alone did
 * not help much either: TBT is measured up to TTI (~13s here), so the late
 * scripts still counted. They now wait for the first sign of engagement,
 * staggered, and always through `requestIdleCallback` so the injection never
 * lands inside the interaction frame.
 *
 * Tradeoff (accepted by the owner, 2026-09-26): visitors who never interact —
 * no scroll, tap or key — get no ad impressions and no analytics session. The
 * Google Analytics `config` bootstrap still runs inline in `<head>`, so for a
 * visitor who *does* interact later the queue (config + product events) is
 * drained intact at that moment; without any interaction it is never sent.
 * `NEXT_PUBLIC_{ADSENSE,GTM,CLARITY}_EAGER=true` puts a single tool back on the
 * mount path: it then ignores the interaction gate entirely and skips the
 * `requestIdleCallback` hop, i.e. that script is deliberately back on the
 * critical path. That is the cost of the rollback, not a bug.
 */

const ADSENSE_SRC =
  "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6636417287024414";
const GTM_SRC = "https://www.googletagmanager.com/gtag/js?id=G-7GCLCCR689";
const CLARITY_ID = "uqtlm9nmco";

/** `NEXT_PUBLIC_*_EAGER=true` falls back to "load as soon as the client mounts". */
const EAGER_ADSENSE = process.env.NEXT_PUBLIC_ADSENSE_EAGER === "true";
const EAGER_GTM = process.env.NEXT_PUBLIC_GTM_EAGER === "true";
const EAGER_CLARITY = process.env.NEXT_PUBLIC_CLARITY_EAGER === "true";

type Loader = {
  /** `src` fragment used to stay idempotent across mounts. */
  marker: string;
  /** Stagger: the three tools must not all start in the same idle callback. */
  delayMs: number;
  eager: boolean;
  load: () => void;
};

function appendScript(src: string, crossOrigin?: string) {
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  if (crossOrigin) script.crossOrigin = crossOrigin;
  document.head.appendChild(script);
}

const loaders: Loader[] = [
  {
    marker: "adsbygoogle.js",
    delayMs: 0,
    eager: EAGER_ADSENSE,
    // Plain attributes only: AdSense warns about anything custom on its tag
    // ("AdSense head tag doesn't support data-adsense attribute").
    load: () => appendScript(ADSENSE_SRC, "anonymous"),
  },
  {
    marker: "googletagmanager.com/gtag/js",
    delayMs: 1000,
    eager: EAGER_GTM,
    load: () => appendScript(GTM_SRC),
  },
  {
    marker: "clarity.ms/tag/",
    delayMs: 2000,
    eager: EAGER_CLARITY,
    // The vendor's own snippet, run on demand instead of in <head>.
    load: () =>
      appendInlineScript(
        `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${CLARITY_ID}");`,
      ),
  },
];

function appendInlineScript(source: string) {
  const script = document.createElement("script");
  script.textContent = source;
  document.head.appendChild(script);
}

/**
 * One load per script per page load, across remounts (locale switches remount the
 * layout): without this, a second mount could re-run the Clarity snippet and
 * start a second recording of the same visitor.
 */
const loadedMarkers = new Set<string>();

export function DeferredThirdParty() {
  useEffect(() => {
    let done = false;
    const timeouts: number[] = [];

    const run = (loader: Loader, immediate = false) => {
      if (loadedMarkers.has(loader.marker)) return;
      loadedMarkers.add(loader.marker);

      if (immediate || !("requestIdleCallback" in window)) {
        loader.load();
        return;
      }
      window.requestIdleCallback(() => loader.load(), { timeout: 2000 });
    };

    // `NEXT_PUBLIC_*_EAGER=true` is a real kill switch: those scripts ignore the
    // interaction gate and load as soon as the client mounts. Everything else
    // waits for the first interaction, staggered so one tap never pays for all
    // of them at once.
    for (const loader of loaders) {
      if (loader.eager) {
        timeouts.push(window.setTimeout(() => run(loader, true), 0));
      }
    }

    const gated = loaders.filter((loader) => !loader.eager);

    const start = () => {
      if (done) return;
      done = true;
      cleanup();

      for (const loader of gated) {
        timeouts.push(
          window.setTimeout(() => run(loader), loader.delayMs),
        );
      }
    };

    // The interaction set has to include scrolling: this app scrolls an inner
    // container (`main`), and `scroll` does not bubble, so it needs the capture
    // phase to be observed from `window` at all. `wheel`/`touchmove` cover
    // desktop and mobile gestures; anything else is covered by pointer/keyboard.
    const interactionEvents: Array<[keyof WindowEventMap, EventListenerOptions]> =
      [
        ["pointerdown", {}],
        ["keydown", {}],
        ["wheel", {}],
        ["touchstart", {}],
        ["touchmove", {}],
        ["scroll", { capture: true }],
      ];

    const cleanup = () => {
      for (const [event, options] of interactionEvents) {
        window.removeEventListener(event, start, options);
      }
      for (const timeout of timeouts) window.clearTimeout(timeout);
    };

    // Nothing left to wait for: every script is eager, so registering the
    // interaction listeners (or clearing the eager timeouts) would drop them.
    if (gated.length === 0) return cleanup;

    for (const [event, options] of interactionEvents) {
      window.addEventListener(event, start, {
        ...options,
        passive: true,
        once: true,
      });
    }

    return cleanup;
  }, []);

  return null;
}
